import React from 'react';

import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import Authenticated from 'Components/Layouts/Authenticated';
import { Loading } from 'Components/Partials';
import ViewCmsElementRecords from 'Components/Utilities/ViewCmsElementRecords';

import ServiceUsersApi from 'Services/Api/ServiceUsers/ServiceUsers';
import { filterItemsByAccess } from 'Services/BaseHelpers';

import FamilyTab from './Tabs/Family';
import TimesheetsTab from './Tabs/Timesheets';
import NarrativeScheduleTab from './NarrativeSchedule';
import NarrativesTab from './Tabs/Narratives';
import CalendarTab from './Tabs/Calendar';
import MedicationsTab from './Medications';
import MarsTab from './Tabs/Mars';
import ArchiveUserApi from "Services/Api/Admin/ArchiveUser";
import Toast from "Services/Toast";
import { DangerButton, PrimaryButton } from "Components/Button";
import CareDocumentsTab from "Pages/ServiceUsers/Tabs/CareDocumentsTab";
import { User } from "Services";

export default class ServiceUsersShow extends React.Component {
    /**
     * @var tabs
     * @type {array}
     */
    tabs = [
        {
            title: 'Narratives',
            method: () => this.renderNarratives(),
            key: 'narratives'
        },
        {
            title: 'Care Documents',
            method: () => this.renderCareDocuments(),
            key: 'care_documents'
        },
        // {
        //     title: 'Profile',
        //     method: () => this.renderProfile()
        // },
        {
            title: 'Family',
            method: () => this.renderFamily(),
            key: 'family'
        },
        {
            title: 'Timesheets',
            method: () => this.renderTimesheets(),
            key: 'timesheets'
        },
        {
            title: 'Calendar',
            method: () => this.renderCalendar(),
            key: 'calendar',
        },
        {
            title: 'Narrative Schedule',
            method: () => this.renderNarrativeSchedule(),
            is_admin: true,
            key: 'narrative_schedule'
        },
        {
            title: 'Medication Schedule',
            method: () => this.renderMedications(),
            is_admin: true,
            key: 'medications'
        },
        {
            title: 'MARS',
            method: () => this.renderMars(),
            key: 'mars'
        },
        {
            title: 'Narrative History',
            method: () => this.renderNarrativeHistory(),
            key: 'narrative_history'
        },
        {
            title: 'Archive',
            method: () => this.renderArchive(),
            is_admin: true,
            key: 'archive'
        }
    ];

    /**
     * @var state
     */
    state = {
        service_user_id: this.props.match?.params?.user ?? '',
        working: true,
        activeTab: 0,
        user: null,
        mobileDropdownOpen: false,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount() {
        this.loadUser();
        this.openTabFromUrl();
    }

    /**
     * Open the tab named in the address, e.g. ?tab=care_documents from the To Do list.
     *
     * @method openTabFromUrl
     */
    openTabFromUrl = () => {
        const key = new URLSearchParams(this.props.location?.search ?? '').get('tab');

        if (!key) {
            return;
        }

        const index = this.visibleTabs().findIndex(tab => tab.key === key);

        if (index !== -1) {
            this.setState({activeTab: index});
        }
    };

    /**
     * The tabs this person can see.
     *
     * @method visibleTabs
     * @return {array}
     */
    visibleTabs = () => {
        let tabs = filterItemsByAccess(this.tabs);

        if (User.belongsToGroup('Service User Family') && !User.isAdmin) {
            const familyTabs = User.data.family_user_tabs;
            tabs = familyTabs && typeof familyTabs === 'object' ? tabs.filter(tab => familyTabs[tab.key] === true) : [];
        }

        return tabs;
    };

    componentDidUpdate(prevProps, prevState) {
        // Add/remove a global click listener to close mobile dropdown when open
        if (this.state.mobileDropdownOpen && !prevState.mobileDropdownOpen) {
            document.addEventListener('click', this.closeMobileDropdown);
        } else if (!this.state.mobileDropdownOpen && prevState.mobileDropdownOpen) {
            document.removeEventListener('click', this.closeMobileDropdown);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.closeMobileDropdown);
    }

    toggleMobileDropdown = (e) => {
        e.stopPropagation();
        this.setState((s) => ({ mobileDropdownOpen: !s.mobileDropdownOpen }));
    }

    closeMobileDropdown = () => {
        this.setState({ mobileDropdownOpen: false });
    }

    /**
     * @method loadUser
     */
    loadUser = async () => {
        const { service_user_id } = this.state;

        let response;

        if (User && User?.data && User.belongsToGroup('Service User Family') && !User.isAdmin) {
            response = await ServiceUsersApi.get('service-users-family', { id: service_user_id });
        } else {
            response = await ServiceUsersApi.get ( service_user_id );
        }

        if ( response ) {
            this.setState ( {
                working: false,
                user: response.data.data,
            } );
        }
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const { user } = this.state;

        return (
            <Authenticated title={ user?.full_name } titleIcon={ faUsers }>
                { this.renderTabsContainer() }
            </Authenticated>
        )
    }

    /**
     * @method renderTabsContainer
     * @return {JSX.Element}
     */
    renderTabsContainer() {
        const { service_user_id, activeTab } = this.state;

        // Family users only see the tabs switched on for them.
        const tabs = this.visibleTabs();

        return (
            <div className="flex flex-col h-full">
                {/* Mobile: show native select dropdown with current tab highlighted. Hide on md+ */}
                <div className="md:hidden bg-app-light-blue text-app-grey font-bold p-2 relative">
                    { tabs && tabs.length > 0 && (
                        <>
                            <button
                                type="button"
                                className="w-full p-2 bg-white text-black rounded flex items-center justify-between"
                                onClick={ this.toggleMobileDropdown }
                                aria-expanded={ this.state.mobileDropdownOpen }
                            >
                                <span>{ tabs[activeTab]?.title ?? 'Select' }</span>
                                <span className="ml-2">▾</span>
                            </button>

                            { this.state.mobileDropdownOpen && (
                                <div className="absolute left-2 right-2 mt-2 bg-white shadow rounded z-50" onClick={ (e) => e.stopPropagation() }>
                                    { tabs.map((tab, key) => (
                                        <div
                                            key={ key }
                                            className={`px-4 py-2 cursor-pointer ${activeTab === key ? 'bg-gray-100 font-bold' : ''}`}
                                            onClick={ () => this.setState({ activeTab: key, mobileDropdownOpen: false }) }
                                        >
                                            { tab.title }
                                        </div>
                                    )) }
                                </div>
                            ) }
                        </>
                    ) }
                </div>

                {/* Desktop / tablet: show horizontal tabs. Hidden on small screens */}
                <div className="hidden md:flex overflow-x-auto bg-app-light-blue text-app-grey font-bold select-none whitespace-nowrap">
                    {tabs && tabs.length > 0 && tabs.map((tab, key) => (
                        <div
                            className={`px-4 py-2 cursor-pointer uppercase text-center flex-none inline-block border-b-4 ${activeTab === key ? 'border-app-leading' : 'border-transparent'}`}
                            onClick={ () => this.setState({ activeTab: key }) }
                            key={ key }
                        >
                            <div>
                                { tab.title }
                            </div>
                        </div>
                    )) }
                </div>

                {tabs && tabs.length > 0 && (
                    <div className={ `p-6 bg-gray-300 ${ tabs[activeTab].title === 'Calendar' ? 'pt-0' : '' }` }>
                        { tabs[activeTab].method() }
                    </div>
                )}
            </div>
        );
    }

    /**
     * @method renderProfile
     * @return {JSX.Element}
     */
    renderProfile() {
        const { service_user_id, working, user } = this.state;

        let headerCell = 'bg-gray-300 px-4 py-2 border border-app-leading text-center';
        let cell = 'px-4 py-2 border border-app-leading text-center';

        if (working) {
            return <Loading/>;
        }

        return (
            <table className="table-fixed mx-auto">
                <thead>
                <tr>
                    <th className={ headerCell }>First Name</th>
                    <th className={ headerCell }>Last Name</th>
                    <th className={ headerCell }>Enabled</th>
                    <th className={ headerCell }>Created At</th>
                    <th className={ headerCell }>Notes</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td className={ cell }>{ user.first_name }</td>
                    <td className={ cell }>{ user.last_name }</td>
                    <td className={ cell }>{ user.enabled ? 'Yes' : 'No' }</td>
                    <td className={ cell }>{ user.created_at.date_time_timezone }</td>
                    <td className={ cell }>{ user.notes }</td>
                </tr>
                </tbody>
            </table>
        );
    }

    // Add a new method to render the Care Documents tab
    renderCareDocuments() {
        const { user } = this.state;

        return (
            <CareDocumentsTab
                user={ user }
            />
        );
    }

    /**
     * @method renderFamily
     * @return {JSX.Element}
     */
    renderFamily() {
        const { service_user_id } = this.state;

        return (
            <FamilyTab
                service_user_id={ service_user_id }
            />
        );
    }

    /**
     * @method renderTimesheets
     * @return {JSX.Element}
     */
    renderTimesheets() {
        const { service_user_id } = this.state;

        return (
            <TimesheetsTab
                service_user_id={ service_user_id }
            />
        );
    };

    /**
     * @method renderNarrativeSchedule
     * @return {JSX.Element}
     */
    renderNarrativeSchedule() {
        const { service_user_id } = this.state;

        return (
            <NarrativeScheduleTab
                service_user_id={ service_user_id }
            />
        );
    };

    /**
     * @method renderNarratives
     * @return {JSX.Element}
     */
    renderNarratives() {
        const { service_user_id } = this.state;

        return (
            <NarrativesTab
                service_user_id={ service_user_id }
            />
        );
    };

    /**
     * @method renderCalendar
     * @return {JSX.Element}
     */
    renderCalendar() {
        const { service_user_id } = this.state;

        return (
            <CalendarTab
                service_user_id={ service_user_id }
            />
        );
    };

    /**
     * @method renderMedications
     * @return {JSX.Element}
     */
    renderMedications() {
        const { service_user_id } = this.state;

        return (
            <MedicationsTab
                service_user_id={ service_user_id }
            />
        );
    };

    /**
     * @method renderNarrativeHistory
     * @return {JSX.Element}
     */
    renderNarrativeHistory() {
        const { service_user_id } = this.state;

        return (
            <ViewCmsElementRecords
                service_user_id={ service_user_id }
                attach_to_service_user={ true }
                push_to_history={ false }
            />
        );
    };

    /**
     * @method renderMars
     * @return {JSX.Element}
     */
    renderMars = () => {
        const { service_user_id } = this.state;

        return (
            <MarsTab
                service_user_id={ service_user_id }
            />
        );
    }

    /**
     * @method renderArchive
     */
    renderArchive = () => {
        const { user, service_user_id } = this.state;

        if (user?.is_archived) {
            return (
                <div>
                    User is archived
                </div>
            );
        }

        const archive = async () => {
            const { success } = await ArchiveUserApi.patch({
                service_user_id,
            }, { archived: true });

            if (success) {
                Toast.success("User archived successfully");
            }
        };

        return (
            <div>
                <p className="mb-4">Are you sure you want to archive this user?</p>

                <div className="flex items-center w-full">
                    <DangerButton
                        onClick={archive}
                        text="Archive"
                    />
                </div>
            </div>
        );
    };
}
